import {
  useEffect, useState
} from 'react';
import { useMediaQuery } from 'react-responsive';
import { useForm, FormProvider } from 'react-hook-form';
import { crTheme, Text, InfoMessage } from '@comicrelief/component-library';
import { RichTextConverter } from '@/services/richtextConverter/richtextConverter';
import { formatAmount } from '@/services/journeys/utils';

import { useAppDispatch, useAppSelector } from '@/store/hooks';
import { getCurrencySymbol } from '@/locale';
import { resetJourney } from '@/store/reset';
import { useCartContext } from '@/services/cart/CartContext';
import { logger } from '@/services/logger/logger';
import { clearErrorMessage } from '@/features/paymentProcess/transactionSlice';
import { updatePaymentSelectFields } from '@/features/paymentSelect/paymentSelectSlice';
import PaymentErrorText from '@/features/paymentProcess/PaymentErrorText';
import { useJourneyOptions } from '@/services/journeys/useJourneyOptions';
import { useJourneyStartNavigate } from '@/services/journeys/useJourneyStartNavigate';
import { setJourney } from '@/services/journeys/journeysSlice';
import { JourneyName } from '@/services/journeys/types';

import { updateSeo } from '@/services/seo/seoSlice';
import { setGtmEventChange, GTM_EVENTS, setGtmPreviousUserChoice } from '@/services/gtm/gtmSlice';
import { StructuredErrorMessage } from '@/features/generalError';
import { FULFILMENT_TYPES } from '@/constants/constants';

import { formFields, getFormResolver } from './journeySelectFields';
import Moneybuys from '../../components/moneybuys/Moneybuys';
import MoneybuyDescription from '../../components/moneybuys/MoneybuyDescription';
import PostalRewardsCheckbox from './PostalRewardsCheckbox';

import {
  StyledFieldset, AmountInput, SubmitButton, OutlineWrapper
} from '../../components/global/Global.style';
import {
  JourneySelectWrapper, JourneySelectRadioButton, JourneySelectLabel, HeaderImage, ChooseAmountText, IntroCopyWrapper, Subtitle, FundraisingDecimalHintWrapper
} from './JourneySelect.style';

const desktopMediaQuery = crTheme.allBreakpoints('M');

function JourneySelect() {
  const dispatch = useAppDispatch();
  const paymentSelectState = useAppSelector(state => state.paymentSelect);
  const journeyState = useAppSelector(state => state.journeys);
  const gtmState = useAppSelector(state => state.gtm);
  const [moneybuyPosition, setMoneybuyPosition] =  useState<string | null>(null);
  const [pageLoaded, setPageLoaded] = useState(false);
  const [triggerWidgetJourney, setTriggerWidgetJourney] = useState(false);
  const useDesktopImage = useMediaQuery({ query: desktopMediaQuery });
  const urlOverrides = useAppSelector(state => state.urlOverrides);

  const {
    cartId,
    cartData,
    getCartJourneyMoneybuys,
    getCartDefaultMoneybuy,
    getCartJourneyTitle,
    validateWidgetJourney,
    getCartJourneySubtitle,
    getCartJourneyIntroCopy,
    getCartJourneyHeader,
    getCartFulfilmentType,
    getCartDefaultJourney
  } = useCartContext();

  const {
    journeyOptions,
    selectedJourney,
    handleJourneySelect,
  } = useJourneyOptions();

  const journeyStartNavigate = useJourneyStartNavigate();
  const thisJourneysMoneybuys = getCartJourneyMoneybuys();
  const defaultMBConfig = getCartDefaultMoneybuy();
  const defaultJourney = getCartDefaultJourney();
  const isPayin = selectedJourney === 'payin';
  const isAARN = getCartFulfilmentType() === FULFILMENT_TYPES.AARN;
  let submitButtonCopy;

  const formMethods = useForm({
    mode: 'onBlur',
    resolver: getFormResolver(isAARN),
  });

  const {
    register,
    watch,
    setValue,
    handleSubmit,
    trigger,
    formState: {
      errors: formErrors
    },
  } = formMethods;

  useEffect(() => {
    // Init global state on load - reset as we're starting a new journey
    dispatch(resetJourney());
    // TO-DO: This config should maybe be funnelled in via Router, from a centralised file?
    dispatch(updateSeo({ title: 'Donate | Comic Relief', description: 'Donate | Comic Relief' }));
  }, [dispatch]);

  useEffect(() => {
    // Fire our front page GTM event once we've confirmed GTM's ready, we have a journey set AND valid cart details:
    if (gtmState.gtmInstantiated && journeyState.journey && !pageLoaded && cartId && cartData) {
      // As useCart.ts requires _another_ render when updating cartId with an override set in
      // urlOverrides.cartOverride (and we need to following setGtmEventChange call to happen once ONLY),
      // we need to refer to that state directly here too, to make sure the value is available in time on initial mount.
      //
      // Said overriding id is validated within setCartOverrideFromQuery before it's saved to state,
      // so it's perfectly safe to use it here in this way.
      const finalCart = urlOverrides.cartOverride ?? cartId;
      dispatch(setGtmEventChange({ eventType: GTM_EVENTS.FRONT_PAGE_LOAD, cartId: finalCart, cartData }));

      // Prevent any updates to journeyState retriggering this:
      setPageLoaded(true);
    }
  }, [dispatch, gtmState.gtmInstantiated, journeyState.journey, pageLoaded, cartId, cartData, urlOverrides.cartOverride]);

  // Temporary debug to assist devs
  useEffect(() => {
    if (!cartData) return;
    logger.debug({ cartData });
  }, [cartData]);

  useEffect(() => {
    const watchInputs = watch((formValues, { name }) => {
      if (!name) return;

      const thisInputsValue = formValues[name];

      if (name === 'amount') {
        if (thisInputsValue !== undefined && thisInputsValue) {
          if (Number.isNaN(Number(thisInputsValue))) return;

          // As we can't get Yup to stop the user from being able to input more than 2 decimal places, we'll need to
          // handle accordingly ourselves; initially casting to a string and splitting it at any decimal places:
          const theseDecimals = thisInputsValue.toString().split('.')[1];

          // If we've got MORE than 2 decimals, stop the state update in its tracks:
          if (theseDecimals?.length > 2) {
            return;
          }
          // Retrigger validation:
          void trigger();
        }
      }

      dispatch(updatePaymentSelectFields({
        [name]: thisInputsValue,
      }));
    });
    return () => watchInputs.unsubscribe();
  }, [watch, dispatch, setValue, trigger]);

  const onSubmit = () => {
    if (!selectedJourney) {
      logger.error('No journey selected');
      return;
    }

    dispatch(clearErrorMessage());
    // Store the currently-selected manual entry / moneybuy's details separately
    // when we haven't already have an initial value. This allows HandleGtmEvents
    // to use it as comparison to form a 'remove_from_cart' event...
    if (!gtmState.previousUserChoice) {
      dispatch(setGtmPreviousUserChoice({
        previousAmount: paymentSelectState.amount,
        previousPosition: paymentSelectState.currentMoneybuyPosition,
        previousJourney: selectedJourney
      }));
    }

    journeyStartNavigate();
  };

  // Allow moneybuy init flag to re-run when the active cart changes (e.g. site default cart, then ?cartId= override).
  useEffect(() => {
    if (!cartId) return;
    setMoneybuyPosition(null);
  }, [cartId]);

  // Moneybuy Init: Select the default cart on load (when available), once Moneybuys are available and different to any previously saved state:
  useEffect(() => {
    if (thisJourneysMoneybuys === null || defaultMBConfig === null || moneybuyPosition === defaultMBConfig.toString()) return;

    const targetJourney = urlOverrides.givingTypeOverride ?? defaultJourney;

    // Ensure the correct journey is active before initialising moneybuys.
    // Guards against previously seen issues where stale journey state
    // was used to initialise moneybuys.
    if (targetJourney && selectedJourney !== targetJourney) {
      if (urlOverrides.givingTypeOverride) {
        handleJourneySelect(urlOverrides.givingTypeOverride);
      } else {
        dispatch(setJourney(targetJourney as JourneyName));
      }
      return;
    }

    // Use our default moneybuy config if available, otherwise use the 2nd of any 3, or fallback to the 1st:
    const defaultMBPosition = defaultMBConfig ?? (thisJourneysMoneybuys.length === 3 ? 1 : 0);

    // Confirm moneybuy position exists in moneybuys configuration (avoid an error)
    if (!((defaultMBPosition) in thisJourneysMoneybuys)) {
      logger.warn('Default moneybuy misconfiguration');
      return;
    }

    // Set the moneybuy amount itself to correlate (defaultMoney buy is already zero index, so don't need to convert):
    const defaultMBAmount = thisJourneysMoneybuys[defaultMBPosition].amount;

    // Use any amount set via a URL parameter, else use whatever the cart stipulates:
    const thisAmount = urlOverrides.amountOverride ?? defaultMBAmount;

    // Update state to 'select' this moneybuy when we're using the defaultMBAmount:
    if (!urlOverrides.amountOverride) {
      dispatch(updatePaymentSelectFields({ currentMoneybuyPosition: defaultMBPosition }));
    }

    // Update form state. Without this, the form state would not be updated until the user interacts with the form. An amount is displayed, because the redux state is updated, but the form state is not, and the user isn't allowed to submit until the amount is updated. See DF-122.
    setValue('amount', thisAmount, {
      shouldValidate: true,
      shouldDirty: true,
      shouldTouch: true
    });

    // Ensure we've got a valid set of 'widget' params before updating the flag:
    setTriggerWidgetJourney(Boolean(validateWidgetJourney(urlOverrides) && selectedJourney));

    // Finally, update our flag to prevent additional initialisations, casting to a string
    // to catch any potential 'zero' positions being intepretted as 'false' in the render logic:
    setMoneybuyPosition(defaultMBPosition.toString());
  }, [thisJourneysMoneybuys, defaultMBConfig, dispatch, getCartDefaultMoneybuy, moneybuyPosition, setValue, urlOverrides, handleJourneySelect, validateWidgetJourney, selectedJourney, defaultJourney
  ]);

  // Only bounce the user to the next page when we've been given the thumbs-up:
  useEffect(() => {
    if (!triggerWidgetJourney) {
      return;
    }

    // Clear before navigating to prevent possible loops
    setTriggerWidgetJourney(false);
    journeyStartNavigate();
  }, [journeyStartNavigate, triggerWidgetJourney]);

  const moneybuyUpdateCallback = () => {
    // Non-ideal placeholder fix as react-hook-form's own trigger function
    // won't, for SOME reason, fire in this context...
    // So, having to "manually" trigger an onBlur event by focussing and blurring
    // our amount input:
    const elem = document.getElementById('amount');
    elem?.focus();
    setTimeout(() => {
      elem?.blur();
      // Revalidate form to blow away any old input field errors:
      void trigger();
    }, 1);
  };

  const currencySymbol = getCurrencySymbol();

  if (isAARN) {
    submitButtonCopy = `Give ${currencySymbol}${formatAmount(paymentSelectState.amount)} a month`;
  } else {
    submitButtonCopy = `
    ${isPayin ? 'Pay in' : 'Donate'}

    ${paymentSelectState.amount > 0
    ? `${currencySymbol}${formatAmount(paymentSelectState.amount)}`
    : ''}

    ${selectedJourney === 'monthly'
    ? 'a month'
    : isPayin
      ? 'now'
      : 'once'}
    `;
  }

  // Pull header image from active cart:
  const cartHeader = getCartJourneyHeader();
  const headerImageSrc = cartHeader && (useDesktopImage ? cartHeader.desktopUrl : cartHeader.mobileUrl);
  const cartHasMultipleJourneys = (Object.keys(journeyOptions ?? {}).length ?? 0) > 1;
  const thisIntroCopy = getCartJourneyIntroCopy();
  const amountNumeric = Number(paymentSelectState.amount);
  const showFundraisingDecimalHint = !isPayin
    && amountNumeric > 0
    && !Number.isNaN(amountNumeric)
    && !Number.isInteger(amountNumeric);

  return (
    <FormProvider {...formMethods}>

      {/* render as soon as URLs exist (not gated within moneybuyInit) so the request starts earlier;
      Lighthouse was whining about this dragging down our LCP score.
      fetchPriority tells the browser to prioritise it. */}
      {headerImageSrc ? (
        <HeaderImage
          src={headerImageSrc}
          alt={cartHeader?.alt ?? 'Header img'}
          fetchPriority="high"
        />
      ) : null}

      {/* Only render content once it's all processed: */}
      {moneybuyPosition && (
        <form onSubmit={event => void handleSubmit(onSubmit)(event)} noValidate>

          <Text tag="h2" family="Anton" color="black" uppercase data-test="cartTitle">
            {getCartJourneyTitle()}
          </Text>

          <Subtitle tag="h4" family="Montserrat" color="black" $hasIntroCopy={!!thisIntroCopy} data-test="cartSubtitle">
            {getCartJourneySubtitle()}
          </Subtitle>

          {/* Ensure this optional field has been set before trying to use it: */}
          { thisIntroCopy && (
            <IntroCopyWrapper data-test="cartIntro">
              {RichTextConverter(thisIntroCopy)}
            </IntroCopyWrapper>
          )}

          {isAARN && (
            <PostalRewardsCheckbox />
          )}

          <PaymentErrorText />

          {cartHasMultipleJourneys && (
            <StyledFieldset>
              <legend hidden>
                Would you like to donate monthly, make a one-off donation, or pay in your fundraising money?
              </legend>

              <JourneySelectWrapper data-test="journeySelectWrapper">
                {Object.entries(journeyOptions ?? []).map(

                  ([journeyName, journeyData]) => {
                    const isSelected = selectedJourney === journeyName;

                    return (
                      <JourneySelectLabel
                        htmlFor={journeyName}
                        selected={isSelected}
                        key={journeyName}
                      >
                        <JourneySelectRadioButton
                          type="radio"
                          name="journey-type"
                          id={journeyName}
                          value={journeyName}
                          checked={isSelected}
                          onChange={() => handleJourneySelect(journeyName)}
                        />
                        {journeyData.name}
                      </JourneySelectLabel>
                    );
                  }
                )}
              </JourneySelectWrapper>
            </StyledFieldset>
          )}

          <OutlineWrapper>
            <ChooseAmountText tag="h4" textAlign="center">
              {isPayin ? 'Enter an amount to pay in' : 'Choose an amount to give'}
            </ChooseAmountText>

            { thisJourneysMoneybuys && (
              <Moneybuys
                isPayin={isPayin}
                thisJourneysMoneybuys={thisJourneysMoneybuys}
                currentAmount={paymentSelectState.amount}
                updateCallback={moneybuyUpdateCallback}
                currencySymbol={currencySymbol}
              />
            )}

            <AmountInput
              isPayin={isPayin}
              label={isPayin ? 'Enter an amount to pay in' : formFields.amount.donationLabel}
              id={formFields.amount.id}
              errorMsg={formErrors?.amount?.message}
              type={formFields.amount.type}
              value={paymentSelectState.amount}
              prefix={currencySymbol}
              hasError={!!formErrors?.amount?.message}
              {...register(formFields.amount.id)}
            />

            {/* Only show the moneybuy description when the current amount, selected via moneybuy
            button or manual input - when we've got copy associated with it: */}
            { thisJourneysMoneybuys && (
              <MoneybuyDescription
                currencySymbol={currencySymbol}
                isPayin={isPayin}
                currentAmount={paymentSelectState.amount}
                thisJourneysMoneybuys={thisJourneysMoneybuys}
                currentMoneybuyPosition={paymentSelectState?.currentMoneybuyPosition}
                hasError={!!formErrors?.amount?.message}
              />
            )}
          </OutlineWrapper>

          {showFundraisingDecimalHint && (
            <FundraisingDecimalHintWrapper>
              <InfoMessage
                title="Are you trying to pay in your fundraising money?"
                message="If you are, please select the 'Pay in Fundraising' tab above. If not, please continue with your donation."
                dataTestId="decimal-amount-fundraising-hint"
              />
            </FundraisingDecimalHintWrapper>
          )}

          {/* Render structured error message if it contains pipe separator, otherwise render the error message directly below the amount input */}
          {formErrors?.amount?.message?.includes('|') && formErrors.amount.message && (
            <StructuredErrorMessage errorMessage={formErrors.amount.message} />
          )}

          <div>
            <SubmitButton
              type="submit"
              data-test={`donateButton-${selectedJourney}`}
              fullWidth
            >
              { submitButtonCopy}
            </SubmitButton>
          </div>
        </form>
      )}
    </FormProvider>
  );
}

export default JourneySelect;
